import type { ExpandableMenuList, HomeLink } from '@pesto/ui/components/Navigation/Navigation.types';

import { CreditCardIcon } from '@heroicons/react/20/solid';
import { useAuth } from '@pesto/features';
import { ApplicationLayout } from '@pesto/ui/layouts/ApplicationLayout';
import { useTheme } from '@pesto/ui/providers/ThemeProvider';
import { humanize } from '@pesto/utils/src/string/stringHelper';
import { Asterisk, Dna, Home, Rocket, ShoppingCart, Users } from 'lucide-react';
import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { routes } from '../constants/routes';
import { ColorSchemeValue } from '../constants/themes';

export interface ApplicationLayoutProps {
  className?: string;
  children?: React.ReactNode;
  component?: React.ReactNode;
}

export const MCApplicationLayout = (props: ApplicationLayoutProps) => {
  const { children } = props;
  const { signOut } = useAuth();
  const { setTheme, colorScheme, setColorScheme, availableColorSchemes } = useTheme();

  const userMenuItems: ExpandableMenuList = [
    { type: 'separator' },
    { type: 'item', label: 'Settings', onClick: () => console.log('Settings clicked') },
    { type: 'item', label: 'Support', onClick: () => console.log('Support clicked') },
    {
      type: 'sub',
      label: 'Theme',
      items: [
        { type: 'item', label: 'Light', onClick: () => setTheme('light') },
        { type: 'item', label: 'Dark', onClick: () => setTheme('dark') },
        { type: 'item', label: 'System', onClick: () => setTheme('system') },
      ],
    },
    {
      type: 'sub',
      label: 'Color Scheme',
      items: availableColorSchemes.map((scheme) => ({
        type: 'item',
        label: humanize(scheme),
        onClick: () => setColorScheme(scheme),
      })),
    },
    { type: 'separator' },
    { type: 'item', label: 'Logout', onClick: () => signOut() },
  ];

  const menuLinks: ExpandableMenuList = [
    {
      type: 'item',
      href: routes.dashboard,
      label: 'Dashboard',
      icon: Home,
    },

    {
      type: 'item',
      href: routes.creditApplications,
      label: 'Credit Applications',
      icon: ShoppingCart,
      count: 6,
    },

    {
      type: 'item',
      href: routes.users,
      label: 'Users',
      icon: Users,
    },
    {
      type: 'item',
      href: routes.programs,
      label: 'Programs',
      icon: Users,
    },
    {
      type: 'item',
      href: routes.creditEngine,
      label: 'Credit Engine',
      icon: Dna,
    },
    {
      type: 'sub',
      label: 'Other',
      icon: Asterisk,
      items: [
        { type: 'item', label: 'Reports', href: routes.reports },
        { type: 'item', label: 'Legal Links', href: routes.legalLinks },
        { type: 'item', label: 'Looker Studio', href: routes.lookerStudio },
        { type: 'item', label: 'Security Events', href: routes.securityEvents },
      ],
    },
  ];

  const homeLink: HomeLink = {
    name: 'Mission Control',
    href: routes.dashboard,
    icon: Rocket,
  };

  const homeLightLink: HomeLink = {
    name: 'The Admin Panel',
    href: routes.dashboard,
    icon: CreditCardIcon,
  };

  const logo: HomeLink = colorScheme === ColorSchemeValue.HOMELIGHT ? homeLightLink : homeLink;

  const location = useLocation();

  if (location.pathname === '/') {
    return <>{children}</>;
  }

  return (
    <ApplicationLayout userMenuItems={userMenuItems} menuLinks={menuLinks} homeLink={logo}>
      {children}
    </ApplicationLayout>
  );
};
